/**
 * PraetorAI — pure behaviour tree for THE PRAETOR, Stage 9 Phase 3 final boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Three phases (FINALE_DESIGN.md):
 *   Guarded    (HP > 50%)   — shield + baton; guardActive=true;
 *                             shield_bash (telegraphed close) + baton_combo cycle.
 *   Guarded+Adds (25–50%)  — same attacks; summons 2 Praetorian adds once on entry.
 *   Enraged    (HP ≤ 25%)  — guard dropped, relentless; faster cooldowns;
 *                             baton_combo + ground_pound shockwave (AoE, jump to avoid).
 *
 * Guard mechanic: guardActive=true means frontal attacks deal chip damage only.
 * The adapter / combat system must honour this flag.
 * Scored by music B2 "The Establishment".
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Shield-bash: medium-close range, forces positioning to avoid. */
export const BASH_RANGE = 100;
/** Baton combo: close-quarters. */
export const BATON_RANGE = 90;
/** Ground-pound shockwave: wider AoE; player must jump to avoid. */
export const SHOCKWAVE_RANGE = 140;

// ── Timing constants ──────────────────────────────────────────────────────────
/** Slow, telegraphed shield raise before the bash. */
export const BASH_WINDUP_FRAMES = 22;
export const BATON_WINDUP_FRAMES = 14;
/** Long windup — "jump to avoid" window for the player. */
export const GROUND_POUND_WINDUP_FRAMES = 30;
export const PRAETOR_BACKSTEP_FRAMES = 20;
export const PRAETOR_SUMMON_FRAMES = 35;
export const PRAETOR_BACKSTEP_SPEED = 1.1;

// ── Phase thresholds ──────────────────────────────────────────────────────────
/** HP fraction at which Praetorian adds are summoned (once only). */
export const ADDS_THRESHOLD = 0.5;
/** HP fraction at which the Praetor drops guard and enrages. */
export const ENRAGED_THRESHOLD = 0.25;

const BASE_COOLDOWN = 75;
/** Enrage halves the cooldown for relentless pressure. */
const ENRAGED_COOLDOWN_MUL = 0.5;

// ── Types ─────────────────────────────────────────────────────────────────────
export type PraetorPhase = 'guarded' | 'guarded_adds' | 'enraged';

export type PraetorAttack = 'shield_bash' | 'baton_combo' | 'ground_pound';

export type PraetorNode =
  | 'scan'
  | 'approach'
  | 'bash_windup'
  | 'baton_windup'
  | 'ground_pound_windup'
  | 'summoning'
  | 'backstep';

export interface PraetorAIState {
  node: PraetorNode;
  phase: PraetorPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  summonFrames: number;
  attackCycle: number;
  addsSummoned: boolean;
  guardActive: boolean;
}

export interface PraetorAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). Drives phase-transition checks each frame. */
  hpFraction: number;
}

export interface PraetorAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: PraetorAttack | null;
  /** True on the single frame the adapter should spawn 2 Praetorian adds. */
  wantsSummonAdds: boolean;
  /** True when frontal hits should deal chip damage only (phases 1–2). */
  guardActive: boolean;
  nextState: PraetorAIState;
}

export function initialPraetorState(): PraetorAIState {
  return {
    node: 'scan',
    phase: 'guarded',
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    addsSummoned: false,
    guardActive: true,
  };
}

/** Resolve phase from HP fraction. */
export function phaseFor(hpFraction: number): PraetorPhase {
  if (hpFraction <= ENRAGED_THRESHOLD) return 'enraged';
  if (hpFraction <= ADDS_THRESHOLD) return 'guarded_adds';
  return 'guarded';
}

/**
 * Advance the Praetor behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns
 * hitboxes / adds based on the output.
 */
export function tickPraetorAI(input: PraetorAIInput, state: PraetorAIState): PraetorAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, false, initialPraetorState());
  }

  // ── Phase resolution ──────────────────────────────────────────────────────
  const newPhase = phaseFor(input.hpFraction);
  const phaseChanged = newPhase !== state.phase;
  const attackCycleBase = phaseChanged ? 0 : state.attackCycle;
  const guardActive = newPhase !== 'enraged';
  const { addsSummoned } = state;

  // ── Entry into guarded_adds: one-time adds summon ─────────────────────────
  if (newPhase === 'guarded_adds' && !addsSummoned && state.node !== 'summoning') {
    return noOp(facing, guardActive, {
      ...state,
      node: 'summoning',
      phase: newPhase,
      addsSummoned: true,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: PRAETOR_SUMMON_FRAMES,
      attackCycle: 0,
      guardActive,
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    // If a hit interrupts the summon wind-up, re-arm the dedup flag: the spawn
    // signal only fires at wind-up completion, so without this the praetorian
    // guard adds are silently never summoned. The entry gate re-fires it on a
    // later clean frame — still exactly once.
    const interrupted = state.node === 'summoning';
    return noOp(facing, guardActive, {
      ...state,
      phase: newPhase,
      addsSummoned: interrupted ? false : addsSummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      summonFrames: 0,
      attackCycle: attackCycleBase,
      guardActive,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, guardActive, {
        ...state,
        phase: newPhase,
        addsSummoned,
        cooldown,
        summonFrames,
        guardActive,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonAdds: true,
      guardActive,
      nextState: {
        ...state,
        node: 'scan',
        phase: newPhase,
        addsSummoned,
        cooldown: cooldownFor(input.aggressionScale, newPhase),
        summonFrames: 0,
        attackCycle: 0,
        guardActive,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * PRAETOR_BACKSTEP_SPEED,
        moveY: away.y * PRAETOR_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonAdds: false,
        guardActive,
        nextState: {
          ...state,
          phase: newPhase,
          addsSummoned,
          cooldown,
          backstepFrames,
          attackCycle: attackCycleBase,
          guardActive,
        },
      };
    }
    return noOp(facing, guardActive, {
      ...state,
      phase: newPhase,
      addsSummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
      attackCycle: attackCycleBase,
      guardActive,
    });
  }

  // ── BASH WINDUP ───────────────────────────────────────────────────────────
  if (state.node === 'bash_windup') {
    if (windup > 0) {
      return noOp(facing, guardActive, {
        ...state,
        phase: newPhase,
        addsSummoned,
        cooldown,
        windup,
        guardActive,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'shield_bash',
      wantsSummonAdds: false,
      guardActive,
      nextState: {
        ...state,
        phase: newPhase,
        addsSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, newPhase),
        windup: 0,
        backstepFrames: PRAETOR_BACKSTEP_FRAMES,
        attackCycle: nextCycle(attackCycleBase),
        guardActive,
      },
    };
  }

  // ── BATON WINDUP ──────────────────────────────────────────────────────────
  if (state.node === 'baton_windup') {
    if (windup > 0) {
      return noOp(facing, guardActive, {
        ...state,
        phase: newPhase,
        addsSummoned,
        cooldown,
        windup,
        guardActive,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'baton_combo',
      wantsSummonAdds: false,
      guardActive,
      nextState: {
        ...state,
        phase: newPhase,
        addsSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, newPhase),
        windup: 0,
        backstepFrames: PRAETOR_BACKSTEP_FRAMES,
        attackCycle: nextCycle(attackCycleBase),
        guardActive,
      },
    };
  }

  // ── GROUND POUND WINDUP (enraged phase only) ──────────────────────────────
  if (state.node === 'ground_pound_windup') {
    if (windup > 0) {
      return noOp(facing, guardActive, {
        ...state,
        phase: newPhase,
        addsSummoned,
        cooldown,
        windup,
        guardActive,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'ground_pound',
      wantsSummonAdds: false,
      guardActive,
      nextState: {
        ...state,
        phase: newPhase,
        addsSummoned,
        node: 'scan',
        cooldown: cooldownFor(input.aggressionScale, newPhase),
        windup: 0,
        attackCycle: nextCycle(attackCycleBase),
        guardActive,
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const attackCycle = attackCycleBase;

  if (cooldown === 0 && input.canAttack) {
    if (newPhase === 'guarded' || newPhase === 'guarded_adds') {
      if (attackCycle === 0 && dist <= BASH_RANGE) {
        return noOp(facing, guardActive, {
          ...state,
          phase: newPhase,
          addsSummoned,
          cooldown,
          node: 'bash_windup',
          windup: BASH_WINDUP_FRAMES,
          attackCycle,
          guardActive,
        });
      }
      if (attackCycle === 1 && dist <= BATON_RANGE) {
        return noOp(facing, guardActive, {
          ...state,
          phase: newPhase,
          addsSummoned,
          cooldown,
          node: 'baton_windup',
          windup: BATON_WINDUP_FRAMES,
          attackCycle,
          guardActive,
        });
      }
    }

    if (newPhase === 'enraged') {
      if (attackCycle === 0 && dist <= BATON_RANGE) {
        return noOp(facing, guardActive, {
          ...state,
          phase: newPhase,
          addsSummoned,
          cooldown,
          node: 'baton_windup',
          windup: BATON_WINDUP_FRAMES,
          attackCycle,
          guardActive,
        });
      }
      if (attackCycle === 1 && dist <= SHOCKWAVE_RANGE) {
        return noOp(facing, guardActive, {
          ...state,
          phase: newPhase,
          addsSummoned,
          cooldown,
          node: 'ground_pound_windup',
          windup: GROUND_POUND_WINDUP_FRAMES,
          attackCycle,
          guardActive,
        });
      }
    }
  }

  // ── Approach / orbit ──────────────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: PraetorNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonAdds: false,
      guardActive,
      nextState: {
        ...state,
        phase: newPhase,
        addsSummoned,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        summonFrames: 0,
        attackCycle,
        guardActive,
      },
    };
  }

  return noOp(facing, guardActive, {
    ...state,
    phase: newPhase,
    addsSummoned,
    cooldown,
    node: 'scan',
    attackCycle,
    guardActive,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number, phase: PraetorPhase): number {
  const mul = phase === 'enraged' ? ENRAGED_COOLDOWN_MUL : 1;
  return Math.round(BASE_COOLDOWN * mul * (1 - aggressionScale * 0.5));
}

function toTarget(input: PraetorAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, guardActive: boolean, nextState: PraetorAIState): PraetorAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonAdds: false,
    guardActive,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 2;
}
